import { Controller } from "@hotwired/stimulus";

type HtmxHistoryEvent = Event & {
    detail?: {
        path?: string;
    };
};

export default class extends Controller<HTMLElement> {
    static targets = ["item"];

    declare readonly itemTargets: HTMLElement[];

    connect(): void {
        this.syncFromLocation = this.syncFromLocation.bind(this);
        this.syncFromHistoryEvent = this.syncFromHistoryEvent.bind(this);

        document.body.addEventListener("htmx:pushedIntoHistory", this.syncFromHistoryEvent);
        document.body.addEventListener("htmx:replacedInHistory", this.syncFromHistoryEvent);
        window.addEventListener("popstate", this.syncFromLocation);

        this.syncFromLocation();
    }

    disconnect(): void {
        document.body.removeEventListener("htmx:pushedIntoHistory", this.syncFromHistoryEvent);
        document.body.removeEventListener("htmx:replacedInHistory", this.syncFromHistoryEvent);
        window.removeEventListener("popstate", this.syncFromLocation);
    }

    private syncFromHistoryEvent(event: Event): void {
        const path = (event as HtmxHistoryEvent).detail?.path || window.location.href;

        requestAnimationFrame(() => {
            this.applyPath(path);
        });
    }

    private syncFromLocation(): void {
        this.applyPath(window.location.href);
    }

    private applyPath(rawUrl: string): void {
        const pathname = this.pathnameFromUrl(rawUrl);

        this.itemTargets.forEach((item) => {
            const active = this.itemMatchesPath(item, pathname);

            item.classList.toggle("sidebar-rail-active", active);
            this.applyCurrentState(item, active);

            if (item.dataset.sidebarNavPressed === "true") {
                item.setAttribute("aria-pressed", active ? "true" : "false");
            }
        });
    }

    private applyCurrentState(item: HTMLElement, active: boolean): void {
        if (item instanceof HTMLAnchorElement) {
            item.setAttribute("aria-current", active ? "page" : "false");
        }
    }

    private itemMatchesPath(item: HTMLElement, pathname: string): boolean {
        const matchType = item.dataset.sidebarNavMatch || "exact";
        const paths = this.matchPaths(item);

        switch (matchType) {
            // A section whose sub-pages all live under one path prefix. Without this the
            // server renders the entry active on a sub-page and the client un-highlights
            // it a frame later, a mismatch that only shows up with JS enabled.
            case "prefix":
                return paths.some((path) => this.pathHasPrefix(pathname, this.pathnameFromUrl(path)));
            case "paths":
                return paths.some((path) => this.pathnameFromUrl(path) === pathname);
            case "exact":
                return paths.some((path) => this.pathnameFromUrl(path) === pathname);
            default:
                return false;
        }
    }

    private pathHasPrefix(pathname: string, rawPrefix: string): boolean {
        const prefix = rawPrefix.replace(/\/+$/, "");
        return pathname === prefix || pathname.startsWith(`${prefix}/`);
    }

    private matchPaths(item: HTMLElement): string[] {
        const paths = this.splitData(item.dataset.sidebarNavPaths);

        if (paths.length > 0) {
            return paths;
        }

        if (item instanceof HTMLAnchorElement) {
            return [item.href];
        }

        return [];
    }

    private splitData(value: string | undefined): string[] {
        return (value || "").split(/\s+/).filter(Boolean);
    }

    private pathnameFromUrl(rawUrl: string): string {
        try {
            return new URL(rawUrl, window.location.origin).pathname;
        } catch (_error) {
            return window.location.pathname;
        }
    }
}
